import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  Grid,
  Button,
  Fab,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Collapse,
} from '@mui/material';
import DnsIcon from '@mui/icons-material/Dns';
import TerminalIcon from '@mui/icons-material/Terminal';
import GroupsIcon from '@mui/icons-material/Groups';
import FolderIcon from '@mui/icons-material/Folder';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LanIcon from '@mui/icons-material/Lan';
import CodeIcon from '@mui/icons-material/Code';
import StorageIcon from '@mui/icons-material/Storage';
import SdStorageIcon from '@mui/icons-material/SdStorage';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { motion } from 'framer-motion';
import { roadmaps, topics as topicCatalog } from './data';
import './learn.css';

const MotionPaper = motion(Paper);
const MotionItem = motion(ListItem);

const TopicPage = () => {
  const navigate = useNavigate();
  const { roadmapId, topicId, sectionId } = useParams();
  const roadmap = useMemo(() => roadmaps.find((r) => r.id === roadmapId), [roadmapId]);
  const topic = topicCatalog[topicId];
  const [copied, setCopied] = useState('');
  const [openCmd, setOpenCmd] = useState('');
  const [showTop, setShowTop] = useState(false);

  const slug = useCallback(
    (str) => str?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || '',
    []
  );

  const headings = (topic?.sections || []).map((s) => ({ id: slug(s.heading), title: s.heading }));

  const getIconForSection = (section) => {
    const h = (section.heading || '').toLowerCase();
    if (h.includes('ec2') || h.includes('setup')) return DnsIcon;
    if (h.includes('basic commands')) return TerminalIcon;
    if (h.includes('advanced commands')) return TerminalIcon;
    if (h.includes('users') || h.includes('group')) return GroupsIcon;
    if (h.includes('file transfer')) return CloudUploadIcon;
    if (h.includes('file management')) return FolderIcon;
    if (h.includes('network')) return LanIcon;
    if (h.includes('awk') || h.includes('grep') || h.includes('sed') || h.includes('pro linux')) return CodeIcon;
    if (h.includes('lvm')) return SdStorageIcon;
    if (h.includes('volume')) return StorageIcon;
    return CodeIcon;
  };

  // Scrolling TOC removed; sections are now routed pages

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop;
      setShowTop(y > 400);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!roadmap || !topic) {
    return (
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Typography variant="h5">Topic not found.</Typography>
      </Container>
    );
  }

  return (
    <div className="topic-page">
    {/* 3D background crystals */}
    <div className="crystal-bg" aria-hidden="true">
      <div className="crystal cube c1">
        <div className="face front" />
        <div className="face back" />
        <div className="face right" />
        <div className="face left" />
        <div className="face top" />
        <div className="face bottom" />
      </div>
      <div className="crystal cube c2">
        <div className="face front" />
        <div className="face back" />
        <div className="face right" />
        <div className="face left" />
        <div className="face top" />
        <div className="face bottom" />
      </div>
    </div>
    {/* Linux infra animated SVG backdrop */}
    <svg className="infra-bg" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      {/* server rack left */}
      <g className="server" transform="translate(60,480)">
        <rect x="0" y="0" rx="8" ry="8" width="180" height="220" fill="rgba(2,6,23,0.65)" stroke="rgba(255,255,255,0.2)" />
        {Array.from({length:5}).map((_,i)=>null)}
        <rect x="20" y="20" width="140" height="30" rx="4" fill="rgba(30,41,59,0.9)" />
        <circle className="light l1" cx="160" cy="35" r="4" />
        <rect x="20" y="60" width="140" height="30" rx="4" fill="rgba(30,41,59,0.9)" />
        <circle className="light l2" cx="160" cy="75" r="4" />
        <rect x="20" y="100" width="140" height="30" rx="4" fill="rgba(30,41,59,0.9)" />
        <circle className="light l3" cx="160" cy="115" r="4" />
        <rect x="20" y="140" width="140" height="30" rx="4" fill="rgba(30,41,59,0.9)" />
        <circle className="light l4" cx="160" cy="155" r="4" />
        <rect x="20" y="180" width="140" height="20" rx="4" fill="rgba(30,41,59,0.9)" />
        <circle className="light l5" cx="160" cy="190" r="4" />
      </g>

      {/* terminal window top-right */}
      <g className="terminal" transform="translate(900,120)">
        <rect x="0" y="0" rx="10" ry="10" width="260" height="160" fill="rgba(2,6,23,0.65)" stroke="rgba(255,255,255,0.2)" />
        <rect x="0" y="0" rx="10" ry="10" width="260" height="28" fill="rgba(15,23,42,0.8)" />
        <circle cx="18" cy="14" r="4" fill="#ef4444" />
        <circle cx="34" cy="14" r="4" fill="#f59e0b" />
        <circle cx="50" cy="14" r="4" fill="#10b981" />
        <rect x="18" y="48" width="200" height="8" rx="2" fill="#93c5fd" opacity="0.85" />
        <rect x="18" y="70" width="168" height="8" rx="2" fill="#a7f3d0" opacity="0.85" />
        <rect x="18" y="92" width="220" height="8" rx="2" fill="#fda4af" opacity="0.85" />
        <rect x="18" y="114" width="140" height="8" rx="2" fill="#fde68a" opacity="0.85" />
        <rect className="cursor blink" x="168" y="68" width="10" height="12" rx="2" fill="#e2e8f0" />
      </g>

      {/* network link with moving packets */}
      <g className="link" transform="translate(280,420)">
        <line x1="0" y1="0" x2="640" y2="0" stroke="#60a5fa" stroke-opacity="0.5" stroke-width="2" />
        <circle className="packet p1" r="5" fill="#60a5fa" />
        <circle className="packet p2" r="5" fill="#34d399" />
        <circle className="packet p3" r="5" fill="#f472b6" />
      </g>

      {/* containers moving */}
      <g className="containers" transform="translate(320,520)">
        <rect className="box b1" x="0" y="0" width="34" height="20" rx="3" />
        <rect className="box b2" x="0" y="30" width="34" height="20" rx="3" />
      </g>
    </svg>
    <Container maxWidth="lg" className="topic-container">
      <Box sx={{ mt: 3, mb: 1 }}>
        <Breadcrumbs aria-label="breadcrumb" className="topic-breadcrumbs">
          <MuiLink component={Link} underline="hover" color="inherit" to="/learn">
            Learn
          </MuiLink>
          <MuiLink component={Link} underline="hover" color="inherit" to={`/learn/${roadmap.id}`}>
            {roadmap.title}
          </MuiLink>
          <Typography color="text.primary">{topic.title}</Typography>
        </Breadcrumbs>
      </Box>

      {/* Animated hero */}
      <MotionPaper
        className="topic-hero"
        elevation={2}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <Typography variant="h3" component="h1" className="topic-hero-title gradient-text">
          {topic.title}
        </Typography>
        <Typography variant="subtitle1" className="topic-hero-subtitle">
          {topic.summary}
        </Typography>
      </MotionPaper>

      {/* Section cards grid (no section selected) */}
      {!sectionId && (
        <Grid container spacing={3} className="section-cards">
          {topic.sections?.map((section) => {
            const id = slug(section.heading);
            const preview = section.summary || section.caption || (section.items?.[0]) || (section.steps?.[0]) || (section.commands?.[0]?.desc) || '';
            const to = `/learn/${roadmap.id}/${topic.id}/${id}`;
            return (
              <Grid item xs={12} sm={6} md={4} key={id}>
                <motion.div whileHover={{ y: -4 }}>
                  <Card className="section-card">
                    <CardActionArea onClick={() => navigate(to)}>
                      {section.type === 'image' && section.src ? (
                        <CardMedia
                          component="img"
                          height="140"
                          image={section.src}
                          alt={section.heading}
                          onError={(e)=>{ e.currentTarget.src = `https://via.placeholder.com/640x320.png?text=${encodeURIComponent(section.heading)}`; }}
                        />
                      ) : (
                        <Box className={`section-card-icon type-${section.type || 'bullets'}`}>
                          {(() => { const IconC = getIconForSection(section); return <IconC sx={{ fontSize: 64, color: '#0ea5e9' }} />; })()}
                        </Box>
                      )}
                      <CardContent>
                        <Typography variant="h6" className="section-card-title">{section.heading}</Typography>
                        {preview && (
                          <Typography variant="body2" color="text.secondary" className="section-card-preview">
                            {typeof preview === 'string' ? preview.replace(/\[(.*?)\]\((.*?)\)/g, '$1') : ''}
                          </Typography>
                        )}
                        <Box sx={{ mt: 1 }}>
                          <Chip size="small" label={(section.type || 'Bullets').toUpperCase()} />
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Single section view */}
      {sectionId && (
        <Grid container spacing={3} className="topic-layout">
          <Grid item xs={12} md={9}>
            {topic.sections?.map((section, idx) => {
              const type = section.type || 'bullets';
              const id = slug(section.heading);
              if (id !== sectionId) return null;
              return (
                <MotionPaper
                  key={section.heading + idx}
                  elevation={2}
                  className="topic-section"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 }}
                >
                  <Typography variant="h6" className="section-title" gutterBottom>
                    {section.heading}
                  </Typography>

                  {section.description && (
                    Array.isArray(section.description) ? (
                      section.description.map((p, i) => (
                        <Typography key={i} variant="body1" className="para-text">{p}</Typography>
                      ))
                    ) : (
                      <Typography variant="body1" className="para-text">{section.description}</Typography>
                    )
                  )}

                  {section.diagram && (
                    <Box className="diagram-wrap">
                      <img src={section.diagram} alt={`${section.heading} diagram`} onError={(e)=>{ e.currentTarget.style.display='none'; }} />
                      {section.diagramCaption && (
                        <Typography variant="caption" color="text.secondary">{section.diagramCaption}</Typography>
                      )}
                    </Box>
                  )}

                  {type === 'image' && (
                    <Box sx={{ my: 1 }}>
                      <img
                        src={section.src}
                        alt={section.alt || section.heading}
                        style={{ width: '100%', borderRadius: 12 }}
                        onError={(e) => {
                          e.currentTarget.src = `https://via.placeholder.com/960x400.png?text=${encodeURIComponent(section.heading)}`;
                        }}
                      />
                      {section.caption && (
                        <Typography variant="caption" color="text.secondary">{section.caption}</Typography>
                      )}
                    </Box>
                  )}

                  {/* Quick points if items present and no long description */}
                  {!section.description && type !== 'commands' && Array.isArray(section.items) && section.items.length > 0 && (
                    <Box className="quick-points">
                      {section.items.slice(0, 3).map((it, i) => (
                        <Typography key={i} variant="body2" className="quick-point">• {typeof it === 'string' ? it.replace(/\[(.*?)\]\((.*?)\)/g, '$1') : it}</Typography>
                      ))}
                    </Box>
                  )}

                  {type === 'steps' && (
                    <Box component="ol" sx={{ pl: 3 }}>
                      {section.steps?.map((s, i) => (
                        <li key={i}>
                          <Typography variant="body1" sx={{ mb: 0.5 }}>{s}</Typography>
                        </li>
                      ))}
                    </Box>
                  )}

                  {type === 'commands' && (
                    <List dense>
                      {section.commands?.map((c, i) => (
                        <MotionItem key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.02 * i }}>
                          <ListItemText
                            primary={
                              <Box className="cmd-row">
                                <code className="cmd-code">{c.cmd}</code>
                                <Box className="cmd-actions">
                                  {c.tags?.map((t) => (<Chip key={t} label={t} size="small" sx={{ mr: 0.5 }} />))}
                                  {c.ex && (
                                    <Tooltip title="Show example">
                                      <IconButton size="small" onClick={() => setOpenCmd(openCmd === c.cmd ? '' : c.cmd)}>
                                        <ExpandMoreIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title={copied === c.cmd ? 'Copied' : 'Copy'}>
                                    <IconButton size="small" onClick={async () => { await navigator.clipboard.writeText(c.cmd); setCopied(c.cmd); setTimeout(() => setCopied(''), 1200); }}>
                                      {copied === c.cmd ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            }
                            secondary={c.desc}
                          />
                          {c.note && (
                            <Typography variant="body2" className="cmd-note">{c.note}</Typography>
                          )}
                          {c.ex && (
                            <Collapse in={openCmd === c.cmd} timeout="auto" unmountOnExit>
                              <Box className="cmd-example">
                                <Typography variant="overline">Example</Typography>
                                <pre><code>{c.ex}</code></pre>
                              </Box>
                            </Collapse>
                          )}
                        </MotionItem>
                      ))}
                    </List>
                  )}

                  {type === 'bullets' && (
                    <List dense>
                      {section.items?.map((it, i) => (
                        <MotionItem key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.05 * i }}>
                          <ListItemText
                            primary={/\[(.*?)\]\((.*?)\)/.test(it) ? (
                              <span dangerouslySetInnerHTML={{ __html: it.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noreferrer noopener">$1</a>') }} />
                            ) : it}
                          />
                        </MotionItem>
                      ))}
                    </List>
                  )}
                </MotionPaper>
              );
            })}

            {/* Section pager across routes */}
            {(() => {
              const ids = headings.map((h) => h.id);
              const idx = ids.indexOf(sectionId);
              const prev = ids[idx - 1];
              const next = ids[idx + 1];
              return (
                <Box className="section-pager" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Button variant="outlined" onClick={() => navigate(`/learn/${roadmap.id}/${topic.id}`)}>All Sections</Button>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" disabled={!prev} onClick={() => prev && navigate(`/learn/${roadmap.id}/${topic.id}/${prev}`)}>Previous</Button>
                    <Button variant="contained" disabled={!next} onClick={() => next && navigate(`/learn/${roadmap.id}/${topic.id}/${next}`)}>Next</Button>
                  </Box>
                </Box>
              );
            })()}

            <Divider sx={{ my: 4 }} />
            <Typography variant="body2" className="topic-footnote">Tip: Bookmark this page and follow the sections in order. Many topics are shared across roadmaps — mastering them accelerates multiple paths.</Typography>
          </Grid>
        </Grid>
      )}
    </Container>
    {showTop && (
      <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }}>
        <Fab
          color="primary"
          aria-label="back to top"
          className="back-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          sx={{ position: 'fixed', right: 24, bottom: { xs: 96, md: 24 }, zIndex: 1200 }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </motion.div>
    )}
  </div>
  );
};

export default TopicPage;
